import { useEffect, useState, useMemo } from "react";
import Pagination from "@/components/dashboard/Pagination";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Upsell {
  id: string;
  name: string;
  required: boolean;
  option_selection_mode: string;
  upsell_options: { id: string; name: string; price: number; duration: number }[];
  service_upsells: { id: string }[];
}

const UpsellsPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [upsells, setUpsells] = useState<Upsell[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Upsell | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const paginatedUpsells = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return upsells.slice(start, start + PAGE_SIZE);
  }, [upsells, page]);

  const fetchUpsells = async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("upsells")
      .select("*, upsell_options(*), service_upsells(id)")
      .eq("organisation_id", profile.organisation_id)
      .order("created_at", { ascending: false });
    setUpsells((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchUpsells();
  }, [profile?.organisation_id]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("upsells").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`${deleteTarget.name} ${t("upsells.deleted")}`);
      setUpsells((prev) => prev.filter((u) => u.id !== deleteTarget.id));
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">{t("upsells.title")}</h1>
        <Button asChild>
          <Link to="/dashboard/upsells/new">
            <Plus className="h-4 w-4 mr-2" />
            {t("upsells.add")}
          </Link>
        </Button>
      </div>

      {upsells.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <p className="text-muted-foreground mb-4">{t("upsells.no_items")}</p>
          <Button asChild>
            <Link to="/dashboard/upsells/new">
              <Plus className="h-4 w-4 mr-2" />
              {t("upsells.add")}
            </Link>
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border bg-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("upsells.col_name")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("upsells.col_options")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("upsells.col_required")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("upsells.col_mode")}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("upsells.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUpsells.map((upsell) => (
                  <tr key={upsell.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate(`/dashboard/upsells/${upsell.id}/edit`)}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground">{upsell.name}</p>
                      <p className="text-xs text-muted-foreground">{t("upsells.used_by").replace("{count}", String(upsell.service_upsells?.length || 0))}</p>
                    </td>
                    <td className="px-4 py-3">
                      {upsell.upsell_options.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {upsell.upsell_options.map((opt) => (
                            <Badge key={opt.id} variant="outline" className="text-[10px] font-normal">
                              {opt.name} {opt.price > 0 && `+€${Number(opt.price).toFixed(2)}`} {opt.duration > 0 && `+${opt.duration}min`}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{upsell.required ? t("upsells.yes") : t("upsells.no")}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{upsell.option_selection_mode === "single" ? t("upsells.single") : t("upsells.multiple")}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild><Link to={`/dashboard/upsells/${upsell.id}/edit`} className="flex items-center"><Edit className="h-4 w-4 mr-2" /> {t("upsells.edit")}</Link></DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(upsell); }}><Trash2 className="h-4 w-4 mr-2" /> {t("upsells.delete")}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {paginatedUpsells.map((upsell) => (
              <div key={upsell.id} className="rounded-xl border border-border bg-card p-4 shadow-card cursor-pointer" onClick={() => navigate(`/dashboard/upsells/${upsell.id}/edit`)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground">{upsell.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {upsell.upsell_options.length} {t("upsells.col_options").toLowerCase()} · {upsell.required ? t("upsells.required") : t("upsells.optional_desc")}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild><Link to={`/dashboard/upsells/${upsell.id}/edit`} className="flex items-center"><Edit className="h-4 w-4 mr-2" /> {t("upsells.edit")}</Link></DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(upsell); }}><Trash2 className="h-4 w-4 mr-2" /> {t("upsells.delete")}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {upsell.upsell_options.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {upsell.upsell_options.map((opt) => (
                      <Badge key={opt.id} variant="outline" className="text-[10px] font-normal">
                        {opt.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
           </div>

          <Pagination currentPage={page} totalItems={upsells.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("upsells.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("upsells.delete_desc_pre")} <span className="font-medium text-foreground">{deleteTarget?.name}</span>{t("upsells.delete_desc_post")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("upsells.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("upsells.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UpsellsPage;
